import React, { useState } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency } from '../../utils/formatters';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { TrendingUp, ChevronLeft, BarChart2, Save, RefreshCw, DollarSign, Zap, Calendar, BarChart3, Target, Award, Shield, Plus, Minus, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import ResultsSummary from '../ResultsSummary';
import DynamicChartSystem from '../charts/DynamicChartSystem';
import ExcelTableV5 from '../ExcelTableV5';
import { getCertificatePhasesSummary } from '../../utils/calculations/certificateEvolution';
import { getDetailedCertificateEvolution } from '../../utils/calculations/certificateEvolution';
import DownloadReportButton from '../DownloadReportButton';
import PatrimonyChart from '../charts/PatrimonyChart';
import IncomeChart from '../charts/IncomeChart';
import CashFlowChart from '../charts/CashFlowChart';
import CompoundInterestMultiplier from '../compoundInterest/CompoundInterestMultiplier';

interface LongTermResultsProps {
  onNext: () => void;
  onPrevious: () => void;
  onReset: () => void;
}

const LongTermResults: React.FC<LongTermResultsProps> = ({ onPrevious, onReset, onNext }) => {
  const { 
    investment, 
    results,
    patrimonyChartVisibility,
    incomeChartVisibility,
    cashFlowChartVisibility,
    togglePatrimonyChartElement,
    toggleIncomeChartElement,
    toggleCashFlowChartElement,
    resetPatrimonyChartVisibility,
    resetIncomeChartVisibility,
    resetCashFlowChartVisibility
  } = useCalculator();
  
  const [showExcelTable, setShowExcelTable] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'compoundInterest' | 'patrimony' | 'income' | 'cashflow' | 'excel'>('summary');
  
  if (!results) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">Calculando resultados...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos de inversión a largo plazo</p>
        </div>
      </div>
    );
  }

  // Get detailed certificate evolution and phases
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  const phases = getCertificatePhasesSummary(certificateEvolution, investment.years);
  
  // Calculate effective price with 25% increase
  const effectiveCertificatePrice = investment.certificateBasePrice * 1.25;
  
  // Get the first year utility (year 6 for long-term calculator)
  const firstYearUtility = results.firstYearUtility || 0;
  
  // Get the price differential
  const priceDifferential = results.priceDifferential || 0;

  if (showExcelTable) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <BarChart2 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Tabla de Evolución Detallada - Largo Plazo</h2>
                <p className="text-blue-100">Análisis detallado año por año de tu inversión a largo plazo</p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <ExcelTableV5 
              hideAdditionalOptions={true} // Hide additional options for this calculator
              initialCertificates={investment.initialCertificates} 
              years={investment.years}
            />
            
            <div className="mt-8 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowExcelTable(false)}
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Volver a Resultados</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="p-8 space-y-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'summary' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Resumen
            </button>
            <button
              onClick={() => setActiveTab('compoundInterest')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'compoundInterest' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Interés Compuesto Multiplicador
            </button>
            <button
              onClick={() => setActiveTab('patrimony')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'patrimony' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Patrimonio
            </button>
            <button
              onClick={() => setActiveTab('income')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'income' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Ingresos
            </button>
            <button
              onClick={() => setActiveTab('cashflow')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'cashflow' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Flujo
            </button>
            <button
              onClick={() => setShowExcelTable(true)}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'excel' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tabla Excel
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'summary' && (
            <>
              {/* 1. Resultados Finales Cosecha Capital */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl shadow-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">Resultados Finales - Certificado a Largo Plazo</h3>
                    <p className="text-xs text-blue-100">Al año {investment.years}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-blue-100 mb-1">Certificados iniciales</div>
                    <div className="text-2xl font-bold">
                      {investment.initialCertificates}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-blue-100 mb-1">Por reinversión</div>
                    <div className="text-2xl font-bold">
                      +{results.certificatesSummary.fromReinvestment}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-blue-100 mb-1">Certificados produciendo</div>
                    <div className="text-2xl font-bold">
                      {phases.producing}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-blue-100 mb-1">En maduración</div>
                    <div className="text-2xl font-bold">
                      {phases.waiting}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-blue-100 mb-1">Ingreso mensual futuro</div>
                    <div className="text-xl font-bold">
                      {formatCurrency(results.finalMonthlyIncome, investment.currencyFormat)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-blue-400">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-blue-100 mb-1">Total de Certificados</div>
                      <div className="text-4xl font-extrabold">
                        {results.certificatesSummary.totalCertificates}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-blue-100 mb-1">Patrimonio final</div>
                      <div className="text-3xl font-bold">
                        {formatCurrency(results.finalPatrimony, investment.currencyFormat)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Long-Term Benefits Section */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-100 rounded-3xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-200 rounded-2xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-indigo-800">Beneficios Especiales a Largo Plazo</h3>
                    <p className="text-xs text-indigo-600">Ventajas exclusivas de esta modalidad</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Price Differential */}
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-indigo-600" />
                      </div>
                      <h4 className="font-medium text-indigo-800">Diferencial de Precio (25%)</h4>
                    </div>
                    <div className="text-2xl font-bold text-indigo-700 mb-1 relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={priceDifferential}
                        currencyFormat={investment.currencyFormat}
                        duration={1.4}
                        staggerDelay={0.1}
                      />
                    </div>
                    <div className="text-xs text-indigo-500 mt-1">
                      Inversión adicional por certificados premium
                    </div>
                  </div>

                  {/* First Year Utility */}
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Zap className="w-4 h-4 text-green-600" />
                      </div>
                      <h4 className="font-medium text-green-800">Utilidad del Primer Año (para ti)</h4>
                    </div>
                    <div className="text-2xl font-bold text-green-700 mb-1 relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={firstYearUtility}
                        currencyFormat={investment.currencyFormat}
                        duration={1.4}
                        staggerDelay={0.1}
                      />
                    </div>
                    <div className="text-xs text-green-500 mt-1">
                      Beneficio directo del primer año de producción
                    </div>
                  </div>

                  {/* Production Timeline */}
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-amber-600" />
                      </div>
                      <h4 className="font-medium text-amber-800">Inicio de Producción</h4>
                    </div>
                    <div className="text-2xl font-bold text-amber-700 mb-1 relative overflow-hidden">
                      Año <AnimatedNumberDisplay
                        value={6}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.05}
                        isCurrency={false}
                      />
                    </div>
                    <div className="text-xs text-amber-500 mt-1">
                      Mes 61 (vs. mes 49 en certificado estándar)
                    </div>
                  </div>

                  {/* ROI Analysis */}
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <h4 className="font-medium text-blue-800">Análisis de Retorno</h4>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-blue-600">Inversión adicional:</span>
                      <div className="text-lg font-bold text-blue-700 relative overflow-hidden">
                        <AnimatedNumberDisplay
                          value={priceDifferential}
                          currencyFormat={investment.currencyFormat}
                          duration={1.2}
                          staggerDelay={0.08}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-blue-600">Retorno directo:</span>
                      <div className="text-lg font-bold text-green-700 relative overflow-hidden">
                        <AnimatedNumberDisplay
                          value={firstYearUtility}
                          currencyFormat={investment.currencyFormat}
                          duration={1.2}
                          staggerDelay={0.08}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-baseline mt-1 pt-1 border-t border-blue-100">
                      <span className="text-sm font-medium text-blue-800">Balance:</span>
                      <div className={`text-lg font-bold relative overflow-hidden ${firstYearUtility >= priceDifferential ? 'text-green-700' : 'text-red-600'}`}>
                        <AnimatedNumberDisplay
                          value={firstYearUtility - priceDifferential}
                          currencyFormat={investment.currencyFormat}
                          duration={1.2}
                          staggerDelay={0.08}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 2. Métricas Detalladas */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <ResultsSummary />
              </div>
              
              {/* 3. Excel Table Button */}
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowExcelTable(true)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  <BarChart2 className="w-5 h-5" />
                  <span>Ver Tabla de Evolución Detallada</span>
                </motion.button>
              </div>
              
              {/* Download Report Button */}
              <DownloadReportButton />
            </>
          )}

          {activeTab === 'compoundInterest' && (
            <div className="min-h-[500px]">
              <CompoundInterestMultiplier />
            </div>
          )}

          {activeTab === 'patrimony' && (
            <div className="min-h-[500px]">
              <PatrimonyChart />
            </div>
          )}

          {activeTab === 'income' && (
            <div className="min-h-[500px]">
              <IncomeChart />
            </div>
          )}

          {activeTab === 'cashflow' && (
            <div className="min-h-[500px]">
              <CashFlowChart />
            </div>
          )}
          
          {/* Add multimedia controls for traditional charts */}
          {(activeTab === 'patrimony' || activeTab === 'income' || activeTab === 'cashflow') && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 text-center">
                Los gráficos de patrimonio, ingresos y flujo están disponibles en la calculadora comparativa
              </p>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onReset}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Comenzar de Nuevo</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LongTermResults;